import { Component } from "react";
import { DateRangePicker, Header, Multiselect, SpaceBetween, Container, Box, Button, Table, Select } from "@cloudscape-design/components";

const PERIOD_OPTIONS = [
	{ label: "Day", value: "day" },
	{ label: "Week", value: "week" },
	{ label: "Month", value: "month" },
];

class GamesMetaTagsStat extends Component {
	constructor(props) {
		super(props);
		this.debounceSearchTimer = null;
		this.state = {
			loading: true,
			metaTags: [],
			stats: [],
			filters: {
				period: PERIOD_OPTIONS[0].value,
				selectMetaTagIds: [],
				date: {
					from: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString(),
					to: new Date().toISOString(),
				},
				page: 1,
				limit: 10,
			},
		};
	}

	formatPeriod = (period) => {
		if (!period) return "-";

		if (period.includes(" - ")) {
			const [start, end] = period.split(" - ");
			const formatDate = (d) => {
				const [y, m, day] = d.split(".");
				return `${day}.${m}.${y}`;
			};
			return `${formatDate(start)} - ${formatDate(end)}`;
		}

		const parts = period.split(".");
		if (parts.length === 3) {
			const [y, m, d] = parts;
			return `${d}.${m}.${y}`;
		}
		if (parts.length === 2) {
			const [y, m] = parts;
			return `${m}.${y}`;
		}

		return period;
	};

	setFilter(options) {
		this.setState((prev) => ({
			...prev,
			filters: {
				...prev.filters,
				...options,
			},
		}));
	}

	getMetaTagsList(search = "") {
		this.props.Interface.request(
			"get",
			{ class: "data", module: "metatags", method: "list" },
			{
				search: search,
				page: 1,
				limit: 30,
			},
		).then((res) => {
			this.setState({ metaTags: res.items });
		});
	}

	getMetaTagsStats() {
		const { date, selectMetaTagIds, period } = this.state.filters;
		this.setState({
			loading: true,
		});
		this.props.Interface.request(
			"get",
			{ class: "data", module: "metatags", method: "stats" },
			{
				date: date,
				metaTags: selectMetaTagIds.map((tag) => tag.value),
				period: period,
			},
		)
			.then((res) => {
				this.setState({
					stats: res,
					loading: false,
				});
			})
			.catch((e) => {
				this.setState((prev) => ({
					loading: false,
				}));
			});
	}

	handleClearFilter() {
		this.setState({
			filters: {
				selectMetaTagIds: [],
				period: PERIOD_OPTIONS[0].value,
				date: {
					from: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString(),
					to: new Date().toISOString(),
				},
				page: 1,
				limit: 10,
			},
		});

		setTimeout(() => {
			this.getMetaTagsStats();
		}, 200);
	}

	componentDidMount() {
		this.getMetaTagsList();
		this.getMetaTagsStats();
	}

	render() {
		const periods = [...new Set(this.state.stats.map((r) => r.date))].sort((a, b) => b.localeCompare(a));

		const aggregated = Object.values(
			this.state.stats.reduce((acc, row) => {
				if (!acc[row.metaTag]) {
					acc[row.metaTag] = {
						metaTag: row.metaTag,
						name: row.name,
						active: row.active,
						translate: row.translate,
						translateType: row.translateType,
						dates: {},
					};
				}

				acc[row.metaTag].dates[row.date] = row.gameTotal;

				return acc;
			}, {}),
		);

		const tableItems = aggregated.map((item) => ({
			...item,
			...item.dates,
		}));

		const totalStats = this.state.stats.reduce((acc, curr) => acc + curr.gameTotal, 0);

		return (
			<Container>
				<Header variant="h3">Stats ({totalStats})</Header>
				<SpaceBetween size="s">
					<SpaceBetween direction="horizontal" size="xs">
						<DateRangePicker
							onChange={({ detail }) => {
								this.setFilter({
									date: {
										from: detail.value.startDate,
										to: detail.value.endDate,
									},
								});

								setTimeout(() => {
									this.getMetaTagsStats();
								}, 200);
							}}
							value={{
								startDate: this.state.filters.date.from || "",
								endDate: this.state.filters.date.to || "",
								type: "absolute",
							}}
							isValidRange={(range) => {
								if (range.type === "absolute") {
									const [startDateWithoutTime] = range.startDate.split("T");
									const [endDateWithoutTime] = range.endDate.split("T");
									if (!startDateWithoutTime || !endDateWithoutTime) {
										return {
											valid: false,
											errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
										};
									}
									if (new Date(range.startDate) - new Date(range.endDate) > 0) {
										return {
											valid: false,
											errorMessage: "The selected date range is invalid. The start date must be before the end date.",
										};
									}
								} else {
									return { valid: false };
								}
							}}
							i18nStrings={{
								todayAriaLabel: "Today",
								nextMonthAriaLabel: "Next month",
								previousMonthAriaLabel: "Previous month",
								customRelativeRangeDurationLabel: "Duration",
								customRelativeRangeDurationPlaceholder: "Enter duration",
								customRelativeRangeOptionLabel: "Custom range",
								customRelativeRangeOptionDescription: "Define a custom range in the past",
								customRelativeRangeUnitLabel: "Unit of time",
								formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
									const start = new Date(startDate);
									const end = new Date(endDate);

									const locale = "en-GB";
									const dateOptions = { year: "numeric", month: "long", day: "numeric" };
									const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

									const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
									const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

									return `${formattedStart} - ${formattedEnd}`;
								},
								formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
								dateTimeConstraintText: "Date and time must be within the selected range.",
								renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
								labelRelativeRange: "Relative range",
								labelAbsoluteRange: "Absolute range",
								dateTimePickerAriaLabel: "Date and time picker",
								dateTimePickerLabel: "Date and Time",
								clearButtonAriaLabel: "Clear date range",
								relativeModeTitle: "Relative mode",
								absoluteModeTitle: "Absolute mode",
								cancelButtonLabel: "Cancel",
								applyButtonLabel: "Apply",
							}}
							placeholder="From/to"
						/>
						<Select
							selectedOption={PERIOD_OPTIONS.find((period) => period.value === this.state.filters.period)}
							onChange={({ detail }) => {
								this.setFilter({ period: detail.selectedOption.value });
								setTimeout(() => {
									this.getMetaTagsStats();
								}, 200);
							}}
							options={PERIOD_OPTIONS}
						/>
					</SpaceBetween>
					<div style={{ minWidth: "300px" }}>
						<Multiselect
							inlineTokens
							selectedOptions={this.state.filters.selectMetaTagIds}
							onChange={({ detail }) => {
								this.setFilter({ selectMetaTagIds: detail.selectedOptions });
								setTimeout(() => {
									this.getMetaTagsStats();
								}, 200);
							}}
							placeholder="Meta Tags"
							filteringType="manual"
							onLoadItems={({ detail }) => {
								if (detail.filteringText === "") {
									this.getMetaTagsList(detail.filteringText);
									return;
								}
								if (detail.filteringText) {
									this.debounceSearchTimer && clearTimeout(this.debounceSearchTimer);
									this.debounceSearchTimer = setTimeout(() => {
										this.getMetaTagsList(detail.filteringText);
									}, 300);
								}
							}}
							options={this.state.metaTags.map((tag) => ({ label: `#${tag.id} ${tag.name}`, value: tag.id }))}
						/>
					</div>
				</SpaceBetween>

				<Box margin={{ top: "l", bottom: "l" }}>
					<Table
						items={tableItems}
						loading={this.state.loading}
						empty={
							<Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
								<SpaceBetween>
									<b>No meta tags</b>
									<Button variant="link" onClick={() => this.handleClearFilter()}>
										Clear filters
									</Button>
								</SpaceBetween>
							</Box>
						}
						columnDefinitions={[
							{
								id: "metaTags",
								header: "Meta Tags",
								cell: (item) => (
									<Button
										onClick={() =>
											this.props.handleShowMetaTagDetail({
												id: item.metaTag,
												name: item.name,
												active: item.active,
												translate: item.translate,
												translateType: item.translateType,
											})
										}
										fontWeight="bold">
										#{item.metaTag} | {item.name}
									</Button>
								),
								width: 500,
								minWidth: 200,
							},
							{
								id: "gameTotal",
								header: "Meta Tag Total",
								cell: (item) => (
									<Box textAlign="center" variant="p" fontWeight="bold">
										{Object.values(item.dates).reduce((acc, curr) => acc + curr, 0)}
									</Box>
								),
							},

							...periods.map((period) => ({
								id: period,
								header: (
									<Box textAlign="center" variant="p" fontWeight="bold">
										{this.formatPeriod(period)}
									</Box>
								),
								cell: (item) => (
									<Box textAlign="center" variant="p" fontWeight="bold">
										{item[period] ?? 0}
									</Box>
								),
							})),
						]}
					/>
				</Box>
				<Container>
					<div style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
						<Box variant="h1" color="text-status-info">
							{totalStats}
						</Box>
						<Box variant="small" color="text-label">
							Total meta tags added to games
						</Box>
					</div>
				</Container>
			</Container>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		return <Component {...props} />;
	};
}

export default withRecoil(GamesMetaTagsStat);
